import {
  type OperatorSlug,
  operatorBrandOrOther,
  operatorSearchText,
  type StationsFeature,
} from '@ev/shared'
import { foldText, scoreMatch, searchTokens } from './fold'

/**
 * The searchable index of sites, built once from `stations.geojson`.
 *
 * There is one notion of "does this site match what was typed" in the app, and
 * it lives here. The filter predicate reads the same folded haystack to decide
 * what the map draws, and the search box reads the same records to decide what
 * to list, so a site the list offers is always a site the map is showing.
 * Before integration the two disagreed: the predicate searched name, city and
 * operator while the result list searched only name and city, so typing an
 * operator's name filtered the map and then listed nothing.
 *
 * Everything is folded once per site when the artifact lands rather than once
 * per site per keystroke. At national scale that is the difference between
 * 9 000 `String.normalize` calls on every character typed and none.
 *
 * The three fields are kept apart rather than concatenated because the ranking
 * weighs them differently; `haystack` is their concatenation, which is all the
 * predicate needs for a plain all-tokens-present test.
 */

export interface SiteDirectoryEntry {
  readonly id: string
  readonly name: string
  readonly city: string
  readonly operator: OperatorSlug
  /** Folded, for ranking. */
  readonly foldedName: string
  readonly foldedCity: string
  /**
   * The brand's display name *and* its registry aliases, folded together.
   *
   * Aliases are in here because the name on a charger is rarely the name in the
   * companies register: ANWIM's sites say MOYA, and typing "moya" has to find
   * them. A site in the `other` bucket uses the name its source published
   * instead of the bucket's label: "Inni operatorzy" is a phrase this app
   * invented, and indexing it would answer a search for a real network with
   * nothing while matching a search nobody types.
   */
  readonly foldedOperator: string
  /** All three joined, for the filter predicate's membership test. */
  readonly haystack: string
}

/** How many results the list renders. Beyond this, refining beats scrolling. */
export const SEARCH_RESULT_LIMIT = 20

/**
 * Folded search text per brand, computed once for the registry rather than once
 * per site: there are thousands of sites and a few dozen brands, and GreenWay's
 * aliases fold to the same string all 919 times.
 */
const FOLDED_OPERATORS = new Map<string, string>()

const foldedOperatorText = (slug: OperatorSlug): string => {
  const cached = FOLDED_OPERATORS.get(slug)
  if (cached !== undefined) return cached

  const folded = foldText(operatorSearchText(operatorBrandOrOther(slug)))
  FOLDED_OPERATORS.set(slug, folded)
  return folded
}

export const toDirectoryEntry = (feature: StationsFeature): SiteDirectoryEntry => {
  const { id, n: name, c: city, op: operator, opn: operatorName } = feature.properties
  const foldedName = foldText(name)
  const foldedCity = foldText(city)
  const foldedOperator =
    operatorName === undefined || operatorName === ''
      ? foldedOperatorText(operator)
      : foldText(operatorName)

  return {
    id,
    name,
    city,
    operator,
    foldedName,
    foldedCity,
    foldedOperator,
    haystack: `${foldedName} ${foldedCity} ${foldedOperator}`,
  }
}

export const buildSiteDirectory = (
  features: readonly StationsFeature[],
): readonly SiteDirectoryEntry[] => features.map(toDirectoryEntry)

/**
 * Search the directory, best match first, restricted to what the filters allow.
 *
 * `eligibleSiteIds` is the map's published match set. `null` means the map has
 * not published yet, in which case the text match stands on its own rather than
 * showing nothing during the first load - offering a result the map is not
 * drawing would fly the camera to an empty patch of Poland.
 *
 * Ties break on name, so the order is stable between keystrokes rather than
 * reshuffling with whatever `Array.prototype.sort` felt like.
 */
export const searchSiteDirectory = (
  directory: readonly SiteDirectoryEntry[],
  query: string,
  eligibleSiteIds: ReadonlySet<string> | null,
  limit: number = SEARCH_RESULT_LIMIT,
): SiteDirectoryEntry[] => {
  const tokens = searchTokens(query)
  if (tokens.length === 0) return []

  const hits: { entry: SiteDirectoryEntry; score: number }[] = []

  directory.forEach((entry) => {
    if (eligibleSiteIds !== null && !eligibleSiteIds.has(entry.id)) return
    const score = scoreMatch(
      { name: entry.foldedName, city: entry.foldedCity, operator: entry.foldedOperator },
      tokens,
    )
    if (score !== null) hits.push({ entry, score })
  })

  hits.sort((a, b) => a.score - b.score || a.entry.name.localeCompare(b.entry.name))
  return hits.slice(0, limit).map((hit) => hit.entry)
}
